import * as THREE from 'three';

/** Display preference for linear lengths. Storage remains world units (metres). */
export type LengthUnit = 'mm' | 'in';

const DEFAULT_DECIMALS: Record<LengthUnit, number> = {
  mm: 0,
  in: 2,
};

// Convert World Coordinates to Metric millimeters
export const convertWorldToMillimeter = (world: number, decimals: number = 0) => {
  const millimeters = world * 1000; // Convert the world units to millimeters
  const factor = Math.pow(10, decimals); // Calculate the factor based on the number of decimals
  return Math.round(millimeters * factor) / factor; // Round the millimeter value based on the factor
};

// Convert World Coordinates to imperial inches
export const convertWorldToInch = (world: number, decimals: number = 0) => {
  const inches = world * 39.3701; // Convert the world units to inches
  const factor = Math.pow(10, decimals); // Calculate the factor based on the number of decimals
  return Math.round(inches * factor) / factor; // Round the inch value based on the factor
};

// Convert World Coordinates to Metric Meter number
export const convertWorldToMeter = (world: number, decimals: number = 0) => {
  const meters = world * 100; // Convert the world units to meters
  const factor = Math.pow(10, decimals); // Calculate the factor based on the number of decimals
  return Math.round(meters * factor) / factor; // Round the meter value based on the factor
}
// Convert World Coordinates to Metric Meter number
export const convertWorld = (world: number, decimals: number = 2) => {
  const worldFormat = world ; // Convert the world units to meters
  const factor = Math.pow(10, decimals); // Calculate the factor based on the number of decimals
  return Math.round(worldFormat * factor) / factor; // Round the meter value based on the factor
}
// Millimeters to World Coordinates
export const convertMillimeterToWorld = (metric: number) => {
  return metric / 1000;
}

// Inches to World Coordinates
export const convertInchToWorld = (imperial: number) => {
  return imperial / 39.3701;
}

// Meters to World Coordinates
export const convertMeterToWorld = (metric: number) => {
  return metric / 100;
}

/**
 * Convert world metres to the active display unit.
 * Defaults: mm → 0 decimals, in → 2 decimals.
 */
export const worldToDisplayLength = (
  world: number,
  unit: LengthUnit,
  decimals: number = DEFAULT_DECIMALS[unit],
): number => {
  return unit === 'in'
    ? convertWorldToInch(world, decimals)
    : convertWorldToMillimeter(world, decimals);
};

/** Convert a display-unit value (mm or inches) back to world metres. */
export const displayLengthToWorld = (value: number, unit: LengthUnit): number => {
  return unit === 'in' ? convertInchToWorld(value) : convertMillimeterToWorld(value);
};

/** Numeric value for on-canvas labels (no thousands separator). */
export const formatDisplayNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
): string => value.toLocaleString(undefined, { useGrouping: false, ...options });

/** Formatted length number without unit suffix (canvas labels). */
export const formatLength = (world: number, unit: LengthUnit): string => {
  return formatDisplayNumber(worldToDisplayLength(world, unit));
};

/** Short label for the active length unit. */
export const lengthUnitLabel = (unit: LengthUnit): string => (unit === 'in' ? 'in' : 'mm');

/**
 * Convert a millimeter value (e.g. room-picker params) to the active display unit.
 * Room picker stores dimensions in mm; display follows mm | in preference.
 */
export const mmToDisplayLength = (
  millimeters: number,
  unit: LengthUnit,
  decimals: number = DEFAULT_DECIMALS[unit],
): number => worldToDisplayLength(millimeters / 1000, unit, decimals);

/** Convert a display-unit value back to millimeters (for mm-native models). */
export const displayLengthToMm = (value: number, unit: LengthUnit): number =>
  displayLengthToWorld(value, unit) * 1000;

/** Square metres per square foot (exact: 0.3048²). */
const SQ_METERS_PER_SQ_FOOT = 0.09290304;

/**
 * Convert world area (m²) to the display area unit paired with the length preference.
 * mm → m², in → ft².
 */
export const worldToDisplayArea = (
  areaWorldSqMeters: number,
  unit: LengthUnit,
  decimals: number = 2,
): number => {
  const display =
    unit === 'in' ? areaWorldSqMeters / SQ_METERS_PER_SQ_FOOT : areaWorldSqMeters;
  const factor = Math.pow(10, decimals);
  return Math.round(display * factor) / factor;
};

/** Area unit label paired with the length preference. */
export const areaUnitLabel = (unit: LengthUnit): string => (unit === 'in' ? 'ft²' : 'm²');

/** Formatted area with unit suffix (room / measure labels). */
export const formatArea = (areaWorldSqMeters: number, unit: LengthUnit): string => {
  return `${formatDisplayNumber(worldToDisplayArea(areaWorldSqMeters, unit))} ${areaUnitLabel(unit)}`;
};

/** Type guard / normalizer for persisted preference values. */
export const parseLengthUnit = (value: unknown): LengthUnit | null => {
  if (value === 'mm' || value === 'in') return value;
  return null;
};

export const convertWorldToMillimeterVector = (world: THREE.Vector2) => {
  return new THREE.Vector2(convertWorldToMillimeter(world.x), convertWorldToMillimeter(world.y));
};

export const convertMillimeterToWorldVector = (metric: THREE.Vector2) => {
  return new THREE.Vector2(convertMillimeterToWorld(metric.x), convertMillimeterToWorld(metric.y));
};
